import React, { useState, useEffect, useMemo } from 'react';
import { db, getCollectionName, auth } from './firebase';
import { collection, onSnapshot, addDoc, serverTimestamp } from './firestore-compat';

const DISPOSITIVOS = ['Cateter', 'Cânula', 'Sonda', 'Gastrostomia', 'Traqueostomia', 'Curativo', 'Outro'];

function calcDias(dataProxima) {
  if (!dataProxima) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const prox = new Date(dataProxima + 'T00:00:00');
  return Math.floor((prox - hoje) / 86400000);
}
// Retorna o período em dias: usa periodo_dias se existir, senão deriva de proxima − ultima
function derivePeriodo(r) {
  if (r.periodo_dias && Number(r.periodo_dias) > 0) return Number(r.periodo_dias);
  if (r.data_proxima_troca && r.data_ultima_troca) {
    const diff = Math.round(
      (new Date(r.data_proxima_troca + 'T00:00:00') - new Date(r.data_ultima_troca + 'T00:00:00')) / 86400000
    );
    if (diff > 0) return diff;
  }
  return null;
}

// Fórmula: ultima + round(dias/30) meses calendário
// Ex: 20/02 + 180d → 180/30=6 meses → 20/08 (mesmo dia)
function calcPrevista(ultima, dias, proxima) {
  if (!ultima) return '';
  if (dias && Number(dias) > 0) {
    const d = new Date(ultima + 'T00:00:00');
    if (isNaN(d)) return '';
    d.setMonth(d.getMonth() + Math.round(Number(dias) / 30));
    return d.toISOString().split('T')[0];
  }
  // Fallback: deriva período de proxima − ultima (registros antigos sem periodo_dias)
  if (proxima) {
    const u = new Date(ultima + 'T00:00:00');
    const p = new Date(proxima + 'T00:00:00');
    if (!isNaN(u) && !isNaN(p)) {
      const diff = Math.round((p - u) / 86400000);
      if (diff > 0) {
        const r = new Date(ultima + 'T00:00:00');
        r.setMonth(r.getMonth() + Math.round(diff / 30));
        return r.toISOString().split('T')[0];
      }
    }
  }
  return '';
}

const FORM_VAZIO = {
  paciente_nome: '', dispositivo: '', operadora: '', tem_liminar: 'Não',
  data_ultima_troca: '', periodo_dias: '', observacao: '',
};

const TABS = [
  { k: 'geral',  l: 'Visão geral' },
  { k: 'prazo',  l: 'Acompanhamento de prazo' },
  { k: 'input',  l: '+ Registrar Troca' },
];

const S = {
  paper: '#F7F8F6', card: '#FFFFFF', ink: '#17282B', soft: '#51666A',
  petrol: '#0E5A5F', dark: '#0A4347', line: '#DFE5E3',
  alert: '#BE3D2A', alertBg: '#FBEEEB',
  ok: '#2E7D5B', okBg: '#EAF4EF',
  gold: '#8A6215', goldBg: '#FBF3DF',
  neutral: '#EEF1F0', breve: '#C98A12',
  sans: "'IBM Plex Sans', sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

function useIBMPlex() {
  useEffect(() => {
    if (document.getElementById('ibm-plex-font')) return;
    const link = document.createElement('link');
    link.id = 'ibm-plex-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);
}

const inp = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: `1px solid ${S.line}`, fontSize: 14,
  fontFamily: S.sans, outline: 'none', boxSizing: 'border-box',
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 700, color: S.soft,
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em',
};

function BarChart({ data, color }) {
  const max = data[0]?.count || 1;
  const barColor = color || S.alert;
  if (data.length === 0) return <div style={{ color: S.soft, fontSize: 13, padding: '16px 0', textAlign: 'center' }}>Sem dados</div>;
  return (
    <>
      {data.map(item => (
        <div key={item.name} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 36px', gap: 8, alignItems: 'center', marginBottom: 7, fontSize: 13 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: item.color || S.ink }} title={item.name}>{item.name}</span>
          <div style={{ background: S.neutral, borderRadius: 4, height: 14, overflow: 'hidden' }}>
            <div style={{ width: `${(item.count / max) * 100}%`, height: '100%', background: item.color || barColor, borderRadius: 4, minWidth: 2 }} />
          </div>
          <span style={{ fontFamily: S.mono, fontSize: 12, textAlign: 'right', color: S.ink }}>{item.count}</span>
        </div>
      ))}
    </>
  );
}

const MESES_PT = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

function AgendaChart({ data }) {
  const hoje = new Date();
  const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}`;
  const max = Math.max(...data.map(d => d.count), 1);
  if (data.length === 0) return <div style={{ color: S.soft, fontSize: 13, padding: '16px 0', textAlign: 'center' }}>Sem dados</div>;
  return (
    <>
      {data.map(({ key, count }) => {
        const [y, m] = key.split('-');
        const isPast    = key < mesAtual;
        const isCurrent = key === mesAtual;
        const barColor  = isPast ? S.alert : isCurrent ? S.breve : S.petrol;
        const lblColor  = isPast ? S.alert : isCurrent ? S.breve : S.ink;
        const label     = `${MESES_PT[Number(m)-1]}/${y.slice(2)}`;
        return (
          <div key={key} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 30px', gap: 8, alignItems: 'center', marginBottom: 7, fontSize: 12 }}>
            <span style={{ fontFamily: S.mono, color: lblColor, fontWeight: isCurrent ? 700 : 400 }}>{label}</span>
            <div style={{ background: S.neutral, borderRadius: 4, height: 14, overflow: 'hidden' }}>
              <div style={{ width: `${(count/max)*100}%`, height: '100%', background: barColor, borderRadius: 4, minWidth: 2 }} />
            </div>
            <span style={{ fontFamily: S.mono, fontSize: 12, textAlign: 'right', color: S.ink }}>{count}</span>
          </div>
        );
      })}
    </>
  );
}

const DISPOSITIVOS_DESATIVADO = true;

export default function DashboardDispositivos() {
  const COLL = getCollectionName('trocas_dispositivos');
  const user = auth.currentUser;
  useIBMPlex();

  const [registros, setRegistros]   = useState([]);
  const [tab, setTab]               = useState('geral');
  const [fBusca, setFBusca]         = useState('');
  const [fDisp, setFDisp]           = useState('');
  const [fOper, setFOper]           = useState('');
  const [fLim, setFLim]             = useState('');
  const [kpiFilter, setKpiFilter]   = useState('');
  const [sort, setSort]             = useState({ k: 'dias', asc: true });
  const [form, setForm]             = useState(FORM_VAZIO);
  const [saving, setSaving]         = useState(false);
  const [success, setSuccess]       = useState(false);

  const [pzBusca, setPzBusca]     = useState('');
  const [pzDisp, setPzDisp]       = useState('');
  const [pzOper, setPzOper]       = useState('');
  const [pzFiltro, setPzFiltro]   = useState('');

  useEffect(() => {
    if (DISPOSITIVOS_DESATIVADO) return;
    return onSnapshot(collection(db, COLL), snap => {
      setRegistros(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [COLL]);

  const deduped = useMemo(() => {
    const map = {};
    registros.forEach(r => {
      const key = `${r.paciente_nome}||${r.dispositivo}`;
      // mais recente pela data da última troca (as do FTP do IW entram todas no mesmo momento)
      const ts = `${r.data_ultima_troca || ''}|${r.registrado_em || ''}`;
      const atual = map[key] ? `${map[key].data_ultima_troca || ''}|${map[key].registrado_em || ''}` : '';
      if (!map[key] || ts > atual) map[key] = r;
    });
    return Object.values(map);
  }, [registros]);

  const enriched = useMemo(() =>
    deduped.map(r => {
      const prevista = calcPrevista(r.data_ultima_troca, r.periodo_dias, r.data_proxima_troca);
      const dias = prevista ? calcDias(prevista) : null;
      const situacao = dias === null ? 'sem_periodicidade'
        : dias < 0  ? 'atraso'
        : dias <= 30 ? 'a_vencer'
        : 'em_dia';
      return { ...r, dias, situacao };
    }), [deduped]);

  const dispOpts = useMemo(() => [...new Set(enriched.map(r => r.dispositivo))].filter(Boolean).sort(), [enriched]);
  const operOpts = useMemo(() => [...new Set(enriched.map(r => r.operadora))].filter(Boolean).sort(), [enriched]);
  const limOpts  = useMemo(() => [...new Set(enriched.map(r => r.tem_liminar))].filter(Boolean).sort(), [enriched]);

  const baseFiltered = useMemo(() =>
    enriched.filter(r =>
      (!fBusca || r.paciente_nome?.toLowerCase().includes(fBusca.toLowerCase())) &&
      (!fDisp  || r.dispositivo === fDisp) &&
      (!fOper  || r.operadora === fOper) &&
      (!fLim   || r.tem_liminar === fLim)
    ), [enriched, fBusca, fDisp, fOper, fLim]);

  const filtered = useMemo(() => {
    let list = baseFiltered.filter(r => {
      if (kpiFilter === 'atraso')          return r.situacao === 'atraso';
      if (kpiFilter === 'a_vencer')        return r.situacao === 'a_vencer';
      if (kpiFilter === 'em_dia')          return r.situacao === 'em_dia';
      if (kpiFilter === 'sem_periodicidade') return r.situacao === 'sem_periodicidade';
      return true; // 'all' ou sem filtro → mostra tudo
    });
    return [...list].sort((a, b) => {
      let va, vb;
      if (sort.k === 'p')    { va = a.paciente_nome || '';        vb = b.paciente_nome || ''; }
      else if (sort.k === 'd') { va = a.dispositivo || '';          vb = b.dispositivo || ''; }
      else if (sort.k === 'o') { va = a.operadora || '';            vb = b.operadora || ''; }
      else if (sort.k === 'l') { va = a.tem_liminar || '';          vb = b.tem_liminar || ''; }
      else if (sort.k === 'ut') { va = a.data_ultima_troca || '';   vb = b.data_ultima_troca || ''; }
      else if (sort.k === 'pt')   { va = Number(a.periodo_dias) || 0;                        vb = Number(b.periodo_dias) || 0; }
      else if (sort.k === 'prev') { va = calcPrevista(a.data_ultima_troca, a.periodo_dias, a.data_proxima_troca) || '9'; vb = calcPrevista(b.data_ultima_troca, b.periodo_dias, b.data_proxima_troca) || '9'; }
      else                       { va = a.dias ?? 99999;                                   vb = b.dias ?? 99999; }
      if (typeof va === 'string') return sort.asc ? va.localeCompare(vb) : vb.localeCompare(va);
      return sort.asc ? va - vb : vb - va;
    });
  }, [baseFiltered, kpiFilter, sort]);

  const kpis = useMemo(() => {
    const totalPts = new Set(baseFiltered.map(r => r.paciente_nome)).size;
    const atraso         = baseFiltered.filter(r => r.situacao === 'atraso').length;
    const aVencer        = baseFiltered.filter(r => r.situacao === 'a_vencer').length;
    const emDia          = baseFiltered.filter(r => r.situacao === 'em_dia').length;
    const semPeriodicidade = baseFiltered.filter(r => r.situacao === 'sem_periodicidade').length;
    return [
      { k: 'all',              color: S.soft,  n: totalPts,         lbl: 'Total de pacientes',  sub: `${baseFiltered.length} dispositivos` },
      { k: 'atraso',           color: S.alert, n: atraso,           lbl: 'Vencidos',            sub: 'Troca atrasada — ação imediata' },
      { k: 'a_vencer',         color: S.breve, n: aVencer,          lbl: 'A vencer (30d)',      sub: 'Vence nos próximos 30 dias' },
      { k: 'em_dia',           color: S.ok,    n: emDia,            lbl: 'Em dia',              sub: 'Mais de 30 dias de folga' },
      { k: 'sem_periodicidade',color: S.soft,  n: semPeriodicidade, lbl: 'Sem periodicidade',   sub: 'Período não informado' },
    ];
  }, [baseFiltered]);

  const chartSituacao = useMemo(() => [
    { name: 'Vencidos',       count: baseFiltered.filter(r => r.situacao === 'atraso').length,           color: S.alert },
    { name: 'A vencer (30d)', count: baseFiltered.filter(r => r.situacao === 'a_vencer').length,         color: S.breve },
    { name: 'Em dia',         count: baseFiltered.filter(r => r.situacao === 'em_dia').length,           color: S.ok },
    { name: 'Sem period.',    count: baseFiltered.filter(r => r.situacao === 'sem_periodicidade').length, color: S.soft },
  ].filter(x => x.count > 0), [baseFiltered]);

  const chartByDisp = useMemo(() => {
    const m = {};
    baseFiltered.forEach(r => { m[r.dispositivo || 'Sem tipo'] = (m[r.dispositivo || 'Sem tipo'] || 0) + 1; });
    return Object.entries(m).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [baseFiltered]);

  const chartByOper = useMemo(() => {
    const m = {};
    baseFiltered.forEach(r => { m[r.operadora || 'Sem operadora'] = (m[r.operadora || 'Sem operadora'] || 0) + 1; });
    return Object.entries(m).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [baseFiltered]);

  const chartByPeriodo = useMemo(() => {
    const m = {};
    baseFiltered.forEach(r => {
      const p = derivePeriodo(r);
      const key = p ? `${p}d` : 'N/I';
      m[key] = (m[key] || 0) + 1;
    });
    const order = ['30d','45d','60d','90d','120d','180d','N/I'];
    return [
      ...order.filter(k => m[k]).map(name => ({ name, count: m[name] })),
      ...Object.entries(m).filter(([k]) => !order.includes(k)).map(([name, count]) => ({ name, count })),
    ];
  }, [baseFiltered]);

  const chartAgenda = useMemo(() => {
    const hoje = new Date();
    const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}`;
    const m = {};
    baseFiltered.forEach(r => {
      const prev = calcPrevista(r.data_ultima_troca, r.periodo_dias, r.data_proxima_troca);
      if (!prev) return;
      const key = prev.slice(0, 7); // YYYY-MM
      m[key] = (m[key] || 0) + 1;
    });
    // mostra 6 meses passados + mês atual + futuros (até 18 no total)
    const seisAtras = new Date(hoje);
    seisAtras.setMonth(seisAtras.getMonth() - 6);
    const limInf = `${seisAtras.getFullYear()}-${String(seisAtras.getMonth()+1).padStart(2,'0')}`;
    return Object.entries(m)
      .filter(([k]) => k >= limInf)
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => a.key.localeCompare(b.key))
      .slice(0, 20);
  }, [baseFiltered]);

  const prazoList = useMemo(() => {
    let list = enriched.filter(r =>
      (!pzBusca || r.paciente_nome?.toLowerCase().includes(pzBusca.toLowerCase())) &&
      (!pzDisp  || r.dispositivo === pzDisp) &&
      (!pzOper  || r.operadora === pzOper)
    );
    if (pzFiltro === 'atraso')   list = list.filter(r => r.situacao === 'atraso');
    else if (pzFiltro === 'a_vencer') list = list.filter(r => r.situacao === 'a_vencer');
    else if (pzFiltro === 'ok')  list = list.filter(r => r.situacao === 'em_dia');
    return [...list].sort((a, b) => (a.dias ?? 99999) - (b.dias ?? 99999));
  }, [enriched, pzBusca, pzDisp, pzOper, pzFiltro]);

  const fmtDate = d => {
    if (!d) return '—';
    const [y, m, dia] = d.split('-');
    return `${dia}/${m}/${y}`;
  };

  function toggleSort(k) {
    setSort(s => s.k === k ? { k, asc: !s.asc } : { k, asc: true });
  }

  function clearFilters() { setFBusca(''); setFDisp(''); setFOper(''); setFLim(''); setKpiFilter(''); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.paciente_nome.trim() || !form.dispositivo) return;
    setSaving(true);
    try {
      await addDoc(collection(db, COLL), {
        ...form,
        registrado_por: user?.email || '',
        registrado_por_uid: user?.uid || '',
        registrado_em: serverTimestamp(),
      });
      setForm(FORM_VAZIO);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) { alert('Erro ao salvar: ' + err.message); }
    finally { setSaving(false); }
  }

  const sel = {
    padding: '8px 10px', border: `1px solid ${S.line}`, borderRadius: 8,
    fontSize: 13.5, fontFamily: S.sans, background: '#fff', outline: 'none', minWidth: 150,
  };

  if (DISPOSITIVOS_DESATIVADO) {
    return (
      <div style={{ padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
        <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Troca de Dispositivos desativado</h3>
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 24px 64px', background: S.paper, minHeight: '100vh', fontFamily: S.sans, color: S.ink, fontSize: 14, lineHeight: 1.45 }}>

      {/* ── Cabeçalho ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 22, borderBottom: `3px solid ${S.petrol}`, paddingBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', color: S.dark }}>Controle de Troca de Dispositivos</h1>
          <div style={{ color: S.soft, fontSize: 13, marginTop: 2 }}>Registro mais recente por paciente e tipo de dispositivo</div>
        </div>
        <div style={{ fontFamily: S.mono, fontSize: 12, color: S.soft, textAlign: 'right' }}>
          Data de referência
          <strong style={{ display: 'block', fontSize: 14, color: S.ink }}>{new Date().toLocaleDateString('pt-BR')}</strong>
        </div>
      </div>

      {/* ── Abas ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            fontFamily: S.sans, fontSize: 14, fontWeight: 600, padding: '10px 20px',
            border: `1px solid ${tab === t.k ? S.petrol : S.line}`,
            borderRadius: '8px 8px 0 0', cursor: 'pointer', borderBottom: 'none',
            background: tab === t.k ? S.card : S.neutral,
            color: tab === t.k ? S.dark : S.soft,
            position: 'relative', top: tab === t.k ? 1 : 0,
          }}>{t.l}</button>
        ))}
      </div>

      {/* ═══════════════ VISÃO GERAL ═══════════════ */}
      {tab === 'geral' && (<>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 22 }}>
          {kpis.map(k => {
            const isActive = kpiFilter === k.k && k.k !== 'all';
            return (
              <div key={k.k}
                onClick={() => setKpiFilter(f => (f === k.k || k.k === 'all') ? '' : k.k)}
                tabIndex={0}
                style={{
                  background: S.card, borderRadius: 10, padding: '16px 18px', cursor: 'pointer',
                  border: `1px solid ${isActive ? S.petrol : S.line}`,
                  boxShadow: isActive ? `0 0 0 2px ${S.petrol} inset` : 'none',
                  transition: 'box-shadow 0.15s, border-color 0.15s', position: 'relative',
                }}>
                <div style={{ fontFamily: S.mono, fontSize: 34, fontWeight: 600, lineHeight: 1.05, color: k.color }}>{k.n}</div>
                <div style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: S.soft, marginTop: 4 }}>{k.lbl}</div>
                <div style={{ fontSize: 12, color: S.soft, marginTop: 2 }}>{k.sub}</div>
                {isActive && <span style={{ position: 'absolute', top: 12, right: 14, fontSize: 11, color: S.petrol, fontWeight: 600 }}>✓ filtrado</span>}
              </div>
            );
          })}
        </div>

        {/* Filtros */}
        <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 18, marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: S.dark, marginBottom: 12 }}>Filtros</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="text" placeholder="Buscar paciente…" value={fBusca} onChange={e => setFBusca(e.target.value)}
              style={{ flex: 1, minWidth: 190, padding: '8px 10px', border: `1px solid ${S.line}`, borderRadius: 8, fontSize: 13.5, fontFamily: S.sans, outline: 'none' }} />
            <select value={fDisp} onChange={e => setFDisp(e.target.value)} style={sel}>
              <option value="">Todos os dispositivos</option>
              {dispOpts.map(o => <option key={o}>{o}</option>)}
            </select>
            <select value={fOper} onChange={e => setFOper(e.target.value)} style={sel}>
              <option value="">Todas as operadoras</option>
              {operOpts.map(o => <option key={o}>{o}</option>)}
            </select>
            <select value={fLim} onChange={e => setFLim(e.target.value)} style={{ ...sel, minWidth: 130 }}>
              <option value="">Liminar: todas</option>
              {limOpts.map(o => <option key={o}>{o}</option>)}
            </select>
            <button onClick={clearFilters} style={{ padding: '8px 6px', background: 'none', border: 'none', cursor: 'pointer', color: S.petrol, fontWeight: 600, fontSize: 13, fontFamily: S.sans }}>Limpar filtros</button>
          </div>
        </div>

        {/* Gráficos — linha 1: situação + agenda mensal */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
          <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: S.dark, marginBottom: 12 }}>Situação das trocas</div>
            <BarChart data={chartSituacao} />
          </div>
          <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: S.dark, marginBottom: 4 }}>Agenda de próximas trocas</div>
            <div style={{ fontSize: 11.5, color: S.soft, marginBottom: 12 }}>
              <span style={{ color: S.alert, fontWeight: 600 }}>■</span> vencido &nbsp;
              <span style={{ color: S.breve, fontWeight: 600 }}>■</span> mês atual &nbsp;
              <span style={{ color: S.petrol, fontWeight: 600 }}>■</span> futuro
            </div>
            <AgendaChart data={chartAgenda} />
          </div>
        </div>

        {/* Gráficos — linha 2: dispositivos + operadoras */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
          <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: S.dark, marginBottom: 12 }}>Dispositivos</div>
            <BarChart data={chartByDisp} color={S.petrol} />
          </div>
          <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: S.dark, marginBottom: 12 }}>Operadoras</div>
            <BarChart data={chartByOper} color={S.petrol} />
          </div>
        </div>

        {/* Gráficos — linha 3: periodicidade */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: S.dark, marginBottom: 12 }}>Distribuição por periodicidade</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
              <BarChart data={chartByPeriodo} color={S.gold} />
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: S.dark }}>Listagem</div>
            <span style={{ fontFamily: S.mono, fontSize: 12.5, color: S.soft }}>{filtered.length} registro{filtered.length !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr>
                  {[
                    { k: 'p',    l: 'Paciente' },
                    { k: 'd',    l: 'Dispositivo' },
                    { k: 'o',    l: 'Operadora' },
                    { k: 'l',    l: 'Liminar' },
                    { k: 'ut',   l: 'Última troca' },
                    { k: 'pt',   l: 'Periodicidade' },
                    { k: 'prev', l: 'Troca Prevista' },
                    { k: 'dias', l: 'Situação' },
                  ].map(h => (
                    <th key={h.k} onClick={() => toggleSort(h.k)} style={{
                      textAlign: 'left', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.05em',
                      color: sort.k === h.k ? S.petrol : S.soft, fontWeight: 700,
                      padding: '8px 10px', borderBottom: `2px solid ${S.line}`,
                      whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
                    }}>
                      {h.l} <span style={{ fontSize: 10 }}>{sort.k === h.k ? (sort.asc ? '▲' : '▼') : ''}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 34, color: S.soft }}>Nenhum registro encontrado com os filtros atuais.</td></tr>
                )}
                {filtered.map(r => {
                  const limSim = r.tem_liminar === 'Sim';
                  const limBadge = limSim
                    ? { bg: S.goldBg, color: S.gold, border: '#E4CD96' }
                    : { bg: S.neutral, color: S.soft, border: 'transparent' };
                  return (
                    <tr key={r.id}
                      onMouseEnter={e => { e.currentTarget.style.background = '#F2F6F5'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                      <td style={{ padding: '9px 10px', borderBottom: `1px solid ${S.line}`, fontWeight: 600 }}>{r.paciente_nome}</td>
                      <td style={{ padding: '9px 10px', borderBottom: `1px solid ${S.line}` }}>{r.dispositivo}</td>
                      <td style={{ padding: '9px 10px', borderBottom: `1px solid ${S.line}`, fontSize: 12.5 }}>{r.operadora || '—'}</td>
                      <td style={{ padding: '9px 10px', borderBottom: `1px solid ${S.line}` }}>
                        <span style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: limBadge.bg, color: limBadge.color, border: `1px solid ${limBadge.border}`, whiteSpace: 'nowrap' }}>
                          {r.tem_liminar === 'Sim' ? '⚖ Sim' : (r.tem_liminar || '—')}
                        </span>
                      </td>
                      <td style={{ padding: '9px 10px', borderBottom: `1px solid ${S.line}`, fontFamily: S.mono, fontSize: 12.5 }}>{fmtDate(r.data_ultima_troca)}</td>
                      <td style={{ padding: '9px 10px', borderBottom: `1px solid ${S.line}`, fontFamily: S.mono, fontSize: 12.5 }}>
                        {(() => {
                          const p = derivePeriodo(r);
                          if (!p) return <span style={{ color: S.soft }}>—</span>;
                          return <span style={{ color: r.periodo_dias ? S.ink : S.soft }}>{p} dias</span>;
                        })()}
                      </td>
                      <td style={{ padding: '9px 10px', borderBottom: `1px solid ${S.line}`, fontFamily: S.mono, fontSize: 12.5 }}>
                        {(() => {
                          const prev = calcPrevista(r.data_ultima_troca, r.periodo_dias, r.data_proxima_troca);
                          if (!prev) return <span style={{ color: S.soft }}>—</span>;
                          const d = calcDias(prev);
                          const color = d < 0 ? S.alert : d <= 30 ? S.breve : S.ok;
                          return <span style={{ color, fontWeight: 600 }}>{fmtDate(prev)}</span>;
                        })()}
                      </td>
                      <td style={{ padding: '9px 10px', borderBottom: `1px solid ${S.line}` }}>
                        {r.dias === null
                          ? <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: S.neutral, color: S.soft, display: 'inline-block' }}>Sem periodicidade</span>
                          : r.dias < 0
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: S.mono, fontWeight: 700, fontSize: 14, color: S.alert }}>
                              {Math.abs(r.dias)}d vencido
                              <span style={{ display: 'inline-block', height: 8, width: Math.min(Math.abs(r.dias) * 0.35, 50), borderRadius: 2, background: S.alert, opacity: 0.8 }} />
                            </span>
                          : r.dias <= 30
                          ? <span style={{ fontFamily: S.mono, fontWeight: 600, fontSize: 14, color: S.breve }}>em {r.dias}d</span>
                          : <span style={{ fontFamily: S.mono, fontSize: 14, color: S.ok }}>em {r.dias}d</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: S.soft, marginTop: 14, lineHeight: 1.5 }}>
            Cada linha representa o registro mais recente de cada combinação paciente + dispositivo (evoluções anteriores foram desconsideradas). "Situação" indica há quantos dias a próxima troca está vencida (em vermelho) ou em quantos dias vencerá (em verde).
          </div>
        </div>
      </>)}

      {/* ═══════════════ ACOMPANHAMENTO DE PRAZO ═══════════════ */}
      {tab === 'prazo' && (
        <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: S.dark, marginBottom: 12 }}>Acompanhamento de prazo</div>

          {/* Filtros prazo */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
            <input type="text" placeholder="Buscar paciente…" value={pzBusca} onChange={e => setPzBusca(e.target.value)}
              style={{ flex: 1, minWidth: 190, padding: '8px 10px', border: `1px solid ${S.line}`, borderRadius: 8, fontSize: 13.5, fontFamily: S.sans, outline: 'none' }} />
            <select value={pzDisp} onChange={e => setPzDisp(e.target.value)} style={sel}>
              <option value="">Todos os dispositivos</option>
              {dispOpts.map(o => <option key={o}>{o}</option>)}
            </select>
            <select value={pzOper} onChange={e => setPzOper(e.target.value)} style={sel}>
              <option value="">Todas as operadoras</option>
              {operOpts.map(o => <option key={o}>{o}</option>)}
            </select>
            <select value={pzFiltro} onChange={e => setPzFiltro(e.target.value)} style={{ ...sel, minWidth: 180 }}>
              <option value="">Todas as situações</option>
              <option value="atraso">Somente vencidos</option>
              <option value="a_vencer">A vencer em 30 dias</option>
              <option value="ok">Em dia (mais de 30d)</option>
            </select>
          </div>

          {/* Legenda */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: S.soft, marginBottom: 14 }}>
            {[
              { color: S.alert, label: 'Vencido' },
              { color: S.breve, label: 'A vencer (30d)' },
              { color: S.ok,    label: 'Em dia (>30d)' },
              { color: S.soft,  label: 'Sem periodicidade' },
            ].map(lg => (
              <span key={lg.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: lg.color, display: 'inline-block', flexShrink: 0 }} />
                {lg.label}
              </span>
            ))}
          </div>

          {/* Cards */}
          {prazoList.length === 0
            ? <div style={{ padding: 34, textAlign: 'center', color: S.soft }}>Nenhum paciente encontrado.</div>
            : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                {prazoList.map(r => {
                  const stColor = r.situacao === 'atraso' ? S.alert
                    : r.situacao === 'a_vencer' ? S.breve
                    : r.situacao === 'em_dia' ? S.ok
                    : S.soft;
                  return (
                    <div key={r.id} style={{
                      background: S.card, border: `1px solid ${S.line}`, borderLeft: `5px solid ${stColor}`,
                      borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8,
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{r.paciente_nome}</div>
                      <div>
                        {r.dias === null
                          ? <span style={{ fontFamily: S.mono, fontSize: 22, fontWeight: 600, color: S.soft }}>—</span>
                          : <span style={{ fontFamily: S.mono, fontSize: 22, fontWeight: 600, color: r.dias < 0 ? S.alert : stColor }}>
                              {Math.abs(r.dias)} dias
                            </span>
                        }
                        <small style={{ display: 'block', fontFamily: S.sans, fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: S.soft, marginTop: 2 }}>
                          {r.dias === null ? 'SEM PERIODICIDADE' : r.dias < 0 ? 'VENCIDO' : r.dias <= 30 ? 'A VENCER' : 'RESTANTES'}
                        </small>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <span style={{ fontSize: 12, padding: '2px 8px', background: '#E4EEEE', color: S.dark, border: '1px solid #C6DADA', borderRadius: 20 }}>{r.dispositivo}</span>
                        {r.tem_liminar === 'Sim' && (
                          <span style={{ fontSize: 12, padding: '2px 8px', background: S.goldBg, color: S.gold, border: '1px solid #E4CD96', borderRadius: 20 }}>⚖ Liminar</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: S.soft }}>{r.operadora || '—'}</div>
                      {(() => {
                        const prev = calcPrevista(r.data_ultima_troca, r.periodo_dias, r.data_proxima_troca);
                        return prev ? (
                          <div style={{ fontFamily: S.mono, fontSize: 12, color: S.soft }}>Troca prevista: {fmtDate(prev)}</div>
                        ) : null;
                      })()}
                    </div>
                  );
                })}
              </div>
          }
          <div style={{ fontSize: 12, color: S.soft, marginTop: 14, lineHeight: 1.5 }}>
            Cards ordenados por urgência: primeiro os vencidos há mais tempo, depois os prazos mais próximos. Pacientes com mais de um dispositivo aparecem em um card por dispositivo.
          </div>
        </div>
      )}

      {/* ═══════════════ REGISTRAR TROCA ═══════════════ */}
      {tab === 'input' && (
        <div style={{ maxWidth: 540 }}>
          <div style={{ background: S.card, border: `1px solid ${S.line}`, borderRadius: 10, padding: 28 }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: S.dark }}>Registrar Troca de Dispositivo</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Paciente *</label>
                <input type="text" placeholder="Nome completo do paciente" value={form.paciente_nome}
                  onChange={e => setForm(f => ({ ...f, paciente_nome: e.target.value }))} style={inp} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Dispositivo *</label>
                <select value={form.dispositivo} onChange={e => setForm(f => ({ ...f, dispositivo: e.target.value }))} style={{ ...inp }}>
                  <option value="">Selecione o dispositivo…</option>
                  {DISPOSITIVOS.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Operadora</label>
                <input type="text" placeholder="Ex: UNIMED, Bradesco Saúde…" value={form.operadora}
                  onChange={e => setForm(f => ({ ...f, operadora: e.target.value }))} style={inp} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={lbl}>Data da última troca</label>
                  <input type="date" value={form.data_ultima_troca}
                    onChange={e => setForm(f => ({ ...f, data_ultima_troca: e.target.value }))} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Periodicidade (dias)</label>
                  <input type="number" min="1" max="730" placeholder="Ex: 180"
                    value={form.periodo_dias}
                    onChange={e => setForm(f => ({ ...f, periodo_dias: e.target.value }))} style={inp} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Possui Liminar?</label>
                <div style={{ display: 'flex', gap: 20 }}>
                  {['Sim', 'Não'].map(v => (
                    <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14, color: S.ink }}>
                      <input type="radio" name="liminar" value={v} checked={form.tem_liminar === v} onChange={() => setForm(f => ({ ...f, tem_liminar: v }))} />
                      {v}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={lbl}>Observação</label>
                <textarea value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} rows={3}
                  placeholder="Observações adicionais…" style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button type="submit" disabled={saving || !form.paciente_nome.trim() || !form.dispositivo}
                  style={{ padding: '10px 24px', borderRadius: 8, border: 'none', fontFamily: S.sans,
                    background: (saving || !form.paciente_nome.trim() || !form.dispositivo) ? '#94a3b8' : S.petrol,
                    color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                  {saving ? 'Salvando…' : 'Registrar Troca'}
                </button>
                {success && <span style={{ color: S.ok, fontWeight: 600, fontSize: 13 }}>✓ Registro salvo!</span>}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
